import React, { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckOutlined, DownloadOutlined } from '@ant-design/icons';
import { NodeAccessApproval, NodeAccessOperation, NodeAccessProfileStatus } from '@prisma/client';
import { Button, ButtonProps, Modal, Result, Spin, Typography } from 'antd';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import sanitize from 'sanitize-filename';
import * as XLSX from 'xlsx-js-style';

import { SnapshotWithDetails } from '@app/domain-t3-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { Common } from '@app/web-pages';

import { useApproveSnapshot, useGetSnapshot } from '../data';

export type SnapshotContract = {
  id: number;
  key: React.Key;
  contractName: string;
  contractNumber: string;
  intelSignedDate: Date;
  operations: {
    operation: NodeAccessOperation;
    count: number;
  }[];
};

export type SnapshotState = {
  snapshot?: SnapshotWithDetails;
  contracts?: Record<string, SnapshotContract>;
  startApproval?: () => void;
};

export const SnapshotContext = React.createContext<SnapshotState | undefined>({});

export const SnapshotProvider = ({ children }) => {
  const { snapshotId } = useParams<'snapshotId'>();
  const [approveModalOpen, setApproveModalOpen] = React.useState(false);
  const startApproval = () => setApproveModalOpen(true);

  const {
    data: snapshot,
    isPending,
    error,
  } = useGetSnapshot(snapshotId, {
    enabled: snapshotId !== undefined,
  });
  const approver = useApproveSnapshot(snapshot?.id);

  const contracts = Object.entries(snapshot?.snapshot ?? {}).reduce(
    (acc, [key, value]) => {
      const record = value.records[0];
      if (record) {
        acc[key] = {
          id: record.contractId,
          key: key,
          contractName: record.contractName,
          contractNumber: record.contractNumber,
          intelSignedDate: record.intelSignedDate,
          operations: [
            {
              operation: NodeAccessOperation.ADD,
              count: value.add,
            },
            {
              operation: NodeAccessOperation.DELETE,
              count: value.remove,
            },
          ],
        };
      }
      return acc;
    },
    {} as SnapshotState['contracts'],
  );

  return isPending && snapshotId ? (
    <Spin size="large" style={{ minHeight: '100vh' }}>
      <div style={{ height: '100vh' }} />
    </Spin>
  ) : !snapshot || error ? (
    <Common.NotFoundPage
      subTitle={
        'Snapshot not found or expired. Please make sure you are visiting the website from the link provided in the latest email.'
      }
      children={null}
    />
  ) : (
    <SnapshotContext.Provider
      value={{
        snapshot,
        contracts,
        startApproval,
      }}
    >
      <Modal
        afterClose={() => approver.reset()}
        open={approveModalOpen}
        title={`Approve Snapshot ${snapshot.id}`}
        closeIcon={null}
        onOk={() => (approver.isSuccess ? setApproveModalOpen(false) : approver.mutate())}
        okButtonProps={{
          loading: approver.isPending,
        }}
        okText={approver.isSuccess ? 'Close' : approver.isError ? 'Retry' : 'Approve'}
        onCancel={() => setApproveModalOpen(false)}
        cancelButtonProps={{
          disabled: approver.isPending,
        }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            {!approver.isSuccess && <CancelBtn />}
            <OkBtn />
          </>
        )}
      >
        {approver.isSuccess ? (
          <Result
            status="success"
            title="Successfully Approved"
            subTitle={`Snapshot has been approved. You don't need to do anything else.`}
          />
        ) : approver.isError ? (
          <Result
            status="error"
            title="Failed to approve"
            subTitle="Something went wrong. Please try again. If the issue persists, please contact support."
          />
        ) : (
          <>
            <Typography.Text>Approving this snapshot will make changes to the included contracts.</Typography.Text>
            <br />
            <Typography.Text type="danger">This action cannot be undone.</Typography.Text>
          </>
        )}
      </Modal>
      {children}
    </SnapshotContext.Provider>
  );
};

export const useSnapshot = () => {
  const context = useContext(SnapshotContext);
  const [xlsxLoading, setXlsxLoading] = useState<Array<string>>([]);
  const [downloadAllLoading, setDownloadAllLoading] = useState<boolean>(false);

  if (!context) {
    throw new Error('useSnapshot must be used within a SnapshotProvider');
  }
  const { snapshot, contracts, startApproval } = context;

  const sanitizeFilename = (filename: string) => {
    return sanitize(filename, { replacement: ' ' }) ?? `file.${filename.split('.')[-1] ?? 'xlsx'}`; // Defaults to a file.xlsx incase of a rarity
  };

  const getApproveButtonProps = (): ButtonProps => ({
    children: !snapshot.approverId ? 'Approve' : 'Approved',
    type: 'primary',
    icon: <CheckOutlined />,
    onClick: () => startApproval(),
    disabled: !!snapshot.approverId,
  });

  const dataColumns: {
    title: string;
    dataIndex: string;
    csvFormatter?: (value: unknown) => string;
  }[] = [
    {
      title: 'Operation',
      dataIndex: 'operation',
    },
    {
      title: 'Status',
      dataIndex: 'status',
    },
    {
      title: 'CW Name',
      dataIndex: 'name',
    },
    {
      title: 'CW Job Title',
      dataIndex: 'jobRole',
    },
    {
      title: 'Email',
      dataIndex: 'email',
    },
    {
      title: 'Project',
      dataIndex: 'dieName',
    },
    {
      title: 'Node',
      dataIndex: 'nodeName',
    },
    {
      title: 'Contract Name',
      dataIndex: 'contractName',
    },
    {
      title: 'Contract Number',
      dataIndex: 'contractNumber',
      // Without this excel will treat the contract number as a number and remove leading zeros and converts to scientific notation.
      csvFormatter: (value: string) => (value ? `\t${value}` : ''),
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      csvFormatter: (date: Date) => formatIsoDate(date, false),
    },
    {
      title: 'Last Updated On',
      dataIndex: 'updatedOn',
      csvFormatter: (date: Date) => formatIsoDate(date, false),
    },
  ];

  const getCsvLinkPropsForContract = (contractId: string) => {
    const contract = contracts[contractId];
    const records = snapshot.snapshot[contractId]?.records;
    if (!contract) {
      console.warn(`Contract with id ${contractId} not found`);
      return {
        data: [],
        style: {
          display: 'none',
        },
        hidden: true,
      };
    }

    const sanitizedName = sanitizeFilename(`${contract.contractName}-${snapshot.id}.csv`);
    return {
      data:
        records?.map((approval) => {
          const row = [];
          dataColumns.forEach((column) => {
            const dataIndex = column.dataIndex as keyof NodeAccessApproval;
            const value = column.csvFormatter ? column.csvFormatter(approval[dataIndex]) : approval[dataIndex];
            row.push(value);
          });
          return row;
        }) ?? [],
      headers: dataColumns.map((column) => column.title as string),
      filename: sanitizedName,
      children: <Button icon={<DownloadOutlined />}>Export to CSV</Button>,
    };
  };

  const generateXlsxWorkbookForContract = (contract: SnapshotContract): XLSX.WorkBook => {
    const records = snapshot.snapshot[contract.id]?.records;

    const wb = XLSX.utils.book_new();
    const data =
      records?.map((approval) => {
        const row = [];
        dataColumns.forEach((column) => {
          const dataIndex = column.dataIndex as keyof NodeAccessApproval;
          const value = column.csvFormatter ? column.csvFormatter(approval[dataIndex]) : approval[dataIndex];
          const rowStyle =
            approval.status === NodeAccessProfileStatus.PENDING
              ? approval.operation === NodeAccessOperation.ADD
                ? { fill: { fgColor: { rgb: 'FFFF99' } } }
                : { fill: { fgColor: { rgb: 'FF9999' } } }
              : undefined;
          row.push({
            v: value,
            t: 's',
            s: rowStyle,
          });
        });
        return row;
      }) ?? [];
    data.unshift(
      dataColumns.map((column) => ({
        v: column.title,
        t: 's',
        s: { font: { bold: true }, fill: { fgColor: { rgb: 'BBBBBB' } } },
      })),
    );
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws);
    return wb;
  };

  const getXlsxButtonPropsForContract = (contractId: string): ButtonProps => {
    const contract = contracts[contractId];
    if (!contract) {
      console.warn(`Contract with id ${contract.id} not found`);
      return {
        style: {
          display: 'none',
        },
      };
    }
    const generateXlsxAndSave = () => {
      const wb = generateXlsxWorkbookForContract(contract);
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

      // Save the file using FileSaver (you can also use browser's download attribute directly)
      const sanitizedName = sanitizeFilename(`${contract.contractName}-${snapshot.id}.xlsx`);
      saveAs(new Blob([wbout]), sanitizedName, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,',
      });
      setXlsxLoading((prev) => prev.filter((id) => id !== contractId));
    };
    return {
      onClick: generateXlsxAndSave,
      children: 'Export',
      icon: <DownloadOutlined />,
      loading: xlsxLoading.includes(contractId),
    };
  };

  const getDownloadAllButtonProps = (): ButtonProps => {
    const generateZip = () => {
      setDownloadAllLoading(true);
      const zip = new JSZip();
      Object.values(contracts).forEach((contract) => {
        const wb = generateXlsxWorkbookForContract(contract);
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const sanitizedName = sanitizeFilename(`${contract.contractName}-${snapshot.id}.xlsx`);
        zip.file(
          sanitizedName,
          new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' }),
          { base64: true },
        );
      });
      zip
        .generateAsync({ type: 'blob' })
        .then((content) => {
          saveAs(content, `Snapshot-${snapshot.id}.zip`);
        })
        .finally(() => {
          setDownloadAllLoading(false);
        });
    };
    return {
      onClick: generateZip,
      children: 'Export All',
      icon: <DownloadOutlined />,
      loading: downloadAllLoading,
    };
  };

  return {
    snapshot,
    contracts,
    startApproval,
    getApproveButtonProps,
    getCsvLinkPropsForContract,
    getXlsxButtonPropsForContract,
    getDownloadAllButtonProps,
  };
};
